import { Check, ChevronDown, ChevronRight, ChevronUp, LayoutGrid, Server } from 'lucide-react';
import React from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { buildForgeAppLinks, buildForgeEnvironmentLinks, LocationLike } from './forgeContextLinks';

/**
 * Two switchers, one per axis: which tool you are in, and which environment it is served from.
 * Each reads as `tool: forge` / `env: production` and opens its own menu. The axes are
 * independent — you change tool for one reason and environment for another — so each gets its
 * own control and its own short menu. Tool comes first, then environment.
 *
 * A mouse opens a menu by hovering its switcher, and moving from one switcher to the other swaps
 * the menus without a click. The menu flies out beside the sidebar, rises above the switcher in the
 * mobile drawer (which has no room beside it), or drops below a top bar, and its options slide in
 * one after another. Keyboard and touch keep the ordinary menu behaviour: Enter, Space or ArrowDown
 * on a focused switcher, or a tap.
 *
 * In the sidebar the two sit on their own rows, styled as footer rows: the same padding, height,
 * type and hover as `.sidebar-footer-link` beside them, and a leading icon naming the axis. On the
 * collapsed rail each row shows only the value's abbreviation (`f`, `prd`); the hover title
 * carries the full name. In a top bar they sit side by side as two columns.
 *
 * Neither is coloured by environment: the environment's name is what says where you are. Every
 * name is lowercase in the data, not via `text-transform`, so the visible text and the accessible
 * name are the same string. The menus mark the current option with a tick.
 *
 * Both menus navigate with real anchors: an environment switch is cross-origin, and a tool switch
 * loads that tool's shell. The document is replaced either way, so no menu close can strand focus.
 */

type SwitcherAxis = 'tool' | 'environment';

export type ForgeContextSwitcherVariant = 'sidebar' | 'drawer' | 'toolbar';

/** Where each variant's menu opens, and the chevron pointing there. */
const MENU_PLACEMENT = {
  sidebar: { side: 'right', sideOffset: 8, Caret: ChevronRight },
  drawer: { side: 'top', sideOffset: 4, Caret: ChevronUp },
  toolbar: { side: 'bottom', sideOffset: 6, Caret: ChevronDown },
} as const;

/** Long enough that a pointer crossing the footer on its way elsewhere opens nothing. */
const HOVER_OPEN_DELAY_MS = 90;
/** Long enough to cross the gap between a switcher and its menu. */
const HOVER_CLOSE_DELAY_MS = 180;

interface ForgeContextOptionProps {
  href: string;
  title: string;
  label: string;
  description: string;
  isActive: boolean;
}

const ForgeContextOption: React.FC<ForgeContextOptionProps & { index: number }> = ({
  href,
  title,
  label,
  description,
  isActive,
  index,
}) => {
  // The description is a description, not part of the name: a menu item called "Forge" reads as
  // "Forge, Labeling tools, task lists and dashboards", not as one run-on sentence.
  const descriptionId = React.useId();

  return (
    <DropdownMenuItem asChild>
      <a
        className={`forge-context-option ${isActive ? 'is-current' : ''}`}
        href={href}
        title={title}
        aria-current={isActive ? 'page' : undefined}
        aria-describedby={descriptionId}
        aria-label={label}
        // Staggers the options' entrance, one after another down the menu.
        style={{ '--forge-context-option-index': index } as React.CSSProperties}
      >
        <span className="forge-context-option-mark" aria-hidden="true">
          {isActive ? <Check /> : null}
        </span>
        <span className="forge-context-option-text">
          <span className="forge-context-option-name">{label}</span>
          <span className="forge-context-option-description" id={descriptionId}>
            {description}
          </span>
        </span>
      </a>
    </DropdownMenuItem>
  );
};

/**
 * Which menu is open, and whether a hover opened it. One state for both switchers, so hovering
 * the second closes the first in the same render instead of leaving two menus overlapping.
 */
const useHoverSwitcherMenus = () => {
  const [openAxis, setOpenAxis] = React.useState<SwitcherAxis | null>(null);
  // A hover-opened menu leaves focus where it was, on open and on close: the pointer is only
  // passing, and focus moved onto a switcher would turn the page's Space and Enter into menu keys.
  // Kept per axis, because a swapped-out menu asks about its focus only after the next one opened.
  const hoverOpenedRef = React.useRef<Record<SwitcherAxis, boolean>>({
    tool: false,
    environment: false,
  });
  // The timers read the open menu from here, since they fire after the render they were set in.
  const openAxisRef = React.useRef<SwitcherAxis | null>(null);
  const timerRef = React.useRef<number>();

  const cancelHover = React.useCallback(() => window.clearTimeout(timerRef.current), []);
  React.useEffect(() => cancelHover, [cancelHover]);

  const showMenu = React.useCallback((axis: SwitcherAxis | null) => {
    openAxisRef.current = axis;
    setOpenAxis(axis);
  }, []);

  const hoverOpen = React.useCallback(
    (axis: SwitcherAxis) => {
      cancelHover();
      timerRef.current = window.setTimeout(() => {
        // A menu opened from the keyboard or by a press stays one: it owns focus, and Escape must
        // still hand focus back to its switcher.
        if (openAxisRef.current === axis) return;
        hoverOpenedRef.current[axis] = true;
        showMenu(axis);
      }, HOVER_OPEN_DELAY_MS);
    },
    [cancelHover, showMenu]
  );

  // Only a menu the hover opened closes when the pointer leaves; one opened from the keyboard or
  // by a press stays until it is dismissed.
  const hoverClose = React.useCallback(
    (axis: SwitcherAxis) => {
      cancelHover();
      if (!hoverOpenedRef.current[axis]) return;
      timerRef.current = window.setTimeout(() => {
        if (openAxisRef.current === axis) showMenu(null);
      }, HOVER_CLOSE_DELAY_MS);
    },
    [cancelHover, showMenu]
  );

  const setOpen = React.useCallback(
    (axis: SwitcherAxis, open: boolean) => {
      cancelHover();
      if (open) hoverOpenedRef.current[axis] = false;
      const current = openAxisRef.current;
      showMenu(open ? axis : current === axis ? null : current);
    },
    [cancelHover, showMenu]
  );

  return { openAxis, hoverOpenedRef, hoverOpen, hoverClose, cancelHover, setOpen };
};

type HoverSwitcherMenus = ReturnType<typeof useHoverSwitcherMenus>;

const isMouse = (event: React.PointerEvent) => event.pointerType === 'mouse';

/**
 * Radix forwards `onOpenAutoFocus` from the dropdown content to the menu's focus scope, but leaves
 * it out of the dropdown's prop types. The switcher tests pin that a hover leaves focus in place.
 */
type MenuContentFocusProps = React.ComponentPropsWithoutRef<typeof DropdownMenuContent> & {
  onOpenAutoFocus?: (event: Event) => void;
};

interface ForgeSwitcherProps {
  axis: SwitcherAxis;
  menus: HoverSwitcherMenus;
  variant: ForgeContextSwitcherVariant;
  /** Leading icon, matching the icon treatment of every other row in the sidebar footer. */
  Icon: React.ComponentType<{ className?: string }>;
  /** The axis as the switcher names it: `tool`, `env`. */
  axisLabel: string;
  /** Full value, shown everywhere but the collapsed rail. */
  label: string;
  /** Abbreviation for the 26px collapsed rail. */
  shortLabel: string;
  /** Hover text, which the collapsed rail relies on to expand the abbreviation. */
  title: string;
  ariaLabel: string;
  menuLabel: string;
  menuNote?: string;
  options: ForgeContextOptionProps[];
}

const ForgeSwitcher: React.FC<ForgeSwitcherProps> = ({
  axis,
  menus,
  variant,
  Icon,
  axisLabel,
  label,
  shortLabel,
  title,
  ariaLabel,
  menuLabel,
  menuNote,
  options,
}) => {
  // `role="menu"` exposes only menuitem/group/separator children, so a bare <p> inside it is
  // sighted-only. Naming it from the content is what puts the sentence in the accessibility tree.
  const noteId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const { openAxis, hoverOpenedRef, hoverOpen, hoverClose, cancelHover, setOpen } = menus;
  const open = openAxis === axis;
  const { side, sideOffset, Caret } = MENU_PLACEMENT[variant];
  const keepFocusWhenHovered = (event: Event) => {
    if (hoverOpenedRef.current[axis]) event.preventDefault();
  };

  return (
    // Non-modal: a modal menu blocks pointer events outside it, and the pointer has to reach the
    // other switcher and the page for hovering to swap or close the menus.
    <DropdownMenu modal={false} open={open} onOpenChange={(next) => setOpen(axis, next)}>
      <DropdownMenuTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className="forge-context-chip"
          title={title}
          aria-label={ariaLabel}
          onPointerEnter={(event) => isMouse(event) && hoverOpen(axis)}
          onPointerLeave={(event) => isMouse(event) && hoverClose(axis)}
          onPointerDown={(event) => {
            // Radix toggles on pointer down, which would close the menu the hover just opened.
            if (open && isMouse(event)) event.preventDefault();
          }}
        >
          <Icon className="forge-context-chip-icon" aria-hidden="true" />
          <span className="forge-context-chip-label">
            <span className="forge-context-chip-axis">{axisLabel}:</span>
            <span className="forge-context-label-full">{label}</span>
            <span className="forge-context-label-short">{shortLabel}</span>
          </span>
          <Caret className="forge-context-chip-caret" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="forge-context-menu duration-200 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
        side={side}
        align="start"
        sideOffset={sideOffset}
        aria-describedby={menuNote ? noteId : undefined}
        onPointerEnter={(event) => isMouse(event) && cancelHover()}
        onPointerLeave={(event) => isMouse(event) && hoverClose(axis)}
        {...({ onOpenAutoFocus: keepFocusWhenHovered } as MenuContentFocusProps)}
        onCloseAutoFocus={keepFocusWhenHovered}
        onPointerDownOutside={(event) => {
          // A press on this menu's own switcher is not a press outside it.
          if (triggerRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
      >
        <DropdownMenuLabel className="forge-context-menu-label">{menuLabel}</DropdownMenuLabel>
        {options.map((option, index) => (
          <ForgeContextOption key={option.href} {...option} index={index} />
        ))}
        {menuNote ? (
          <p className="forge-context-menu-note" id={noteId}>
            {menuNote}
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ForgeContextSwitcher: React.FC<{
  location?: LocationLike;
  variant?: ForgeContextSwitcherVariant;
}> = ({ location = window.location, variant = 'sidebar' }) => {
  const { hasInternalAccess } = useAuthContext();
  const menus = useHoverSwitcherMenus();

  if (!hasInternalAccess) {
    return null;
  }

  const environments = buildForgeEnvironmentLinks(location);
  const apps = buildForgeAppLinks(location, { includeArchon: true });
  const currentEnvironment = environments.find((environment) => environment.isActive);
  // `control` is the catch-all for any path outside /forge, /eval and /archon, so exactly one tool
  // is always active; the first entry is a sane read if that predicate ever loosens.
  const currentApp = apps.find((app) => app.isActive) ?? apps[0];

  // An origin that is neither production, staging, nor a dev port (a preview deploy, say) has no
  // environment to name, and saying so beats highlighting nothing.
  const environmentLabel = currentEnvironment?.label ?? 'unknown';
  const environmentShortLabel = currentEnvironment?.shortLabel ?? '?';

  return (
    <div className={`forge-context-switchers is-${variant}`}>
      <ForgeSwitcher
        axis="tool"
        menus={menus}
        variant={variant}
        Icon={LayoutGrid}
        axisLabel="tool"
        label={currentApp.label}
        shortLabel={currentApp.shortLabel}
        title={`tool: ${currentApp.label}`}
        ariaLabel={`Change tool. Current: ${currentApp.label}.`}
        menuLabel="tool"
        options={apps}
      />
      <ForgeSwitcher
        axis="environment"
        menus={menus}
        variant={variant}
        Icon={Server}
        axisLabel="env"
        label={environmentLabel}
        shortLabel={environmentShortLabel}
        title={`environment: ${environmentLabel}`}
        ariaLabel={`Change environment. Current: ${environmentLabel}.`}
        menuLabel="environment"
        menuNote="Switching environment keeps this page, filters and all."
        options={environments}
      />
    </div>
  );
};
